import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, CardActionArea, Button, Chip, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  CircularProgress, Alert, IconButton, Tooltip,
} from '@mui/material';
import {
  Science as ProtocolIcon, Add as AddIcon, Lock as LockIcon,
  Mic as MicIcon, Forum as ChatIcon, UploadFile as UploadIcon, DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { getProtocolConfig, listProtocols, createProtocol, synthesizeSource, deleteProtocol } from '../api/protocols';
import { useAuth } from '../contexts/AuthContext';

const STATE_COLOR = { draft: 'default', in_review: 'warning', published: 'success', processing: 'info', failed: 'error' };

function CreateDialog({ open, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [mode, setMode] = useState('co_author');
  const [brief, setBrief] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim()) { toast.error('Give the protocol a name'); return; }
    if (mode === 'synthesize' && !file) { toast.error('Choose a source document to synthesize from'); return; }
    setBusy(true);
    try {
      const p = await createProtocol({ name: name.trim(), description: description.trim() || null, mode, brief: brief.trim() || null });
      if (mode === 'synthesize' && file) {
        await synthesizeSource(p.id, file);   // drafts in the background; workbench polls
      }
      toast.success('Protocol created');
      onCreated(p);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create protocol');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle fontWeight={700}>New protocol</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField label="Protocol name" value={name} onChange={(e) => setName(e.target.value)} fullWidth autoFocus
            placeholder="e.g. Adrenal Support Protocol" />
          <TextField label="Short description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth />
          <TextField select label="How do you want to start?" value={mode} onChange={(e) => setMode(e.target.value)} fullWidth>
            <MenuItem value="co_author">Co-author with AI (from a brief)</MenuItem>
            <MenuItem value="capture">Capture a working session (record a discussion)</MenuItem>
            <MenuItem value="synthesize">Synthesize from a source document</MenuItem>
          </TextField>
          {mode === 'co_author' && (
            <TextField label="Brief for the AI (optional)" value={brief} onChange={(e) => setBrief(e.target.value)}
              fullWidth multiline minRows={3}
              placeholder="Describe the protocol you want drafted — goal, phases, eligibility, anything decided so far." />
          )}
          {mode === 'capture' && (
            <Alert severity="info" icon={<MicIcon />}>
              You'll record the discussion inside the protocol workbench. The AI transcribes it and drafts the protocol,
              decisions, recommendations and open questions.
            </Alert>
          )}
          {mode === 'synthesize' && (
            <Box>
              <Button component="label" variant="outlined" startIcon={<UploadIcon />} fullWidth>
                {file ? file.name : 'Choose source (PDF, Word, text)'}
                <input type="file" hidden accept=".pdf,.txt,.md,.rtf,.doc,.docx" onChange={(e) => setFile(e.target.files?.[0] || null)} />
              </Button>
              <Alert severity="info" sx={{ mt: 1.5 }}>
                The AI reads the document and drafts a protocol with citations back to the source.
              </Alert>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={busy}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}>
          {busy ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function ProtocolLibrary() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isClinicAdmin = user?.role === 'clinic_admin';
  const [config, setConfig] = useState(null);
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);   // protocol pending deletion
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await deleteProtocol(confirmDelete.id);
      setProtocols((list) => list.filter((x) => x.id !== confirmDelete.id));
      toast.success('Protocol deleted');
      setConfirmDelete(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete protocol');
    } finally {
      setDeleting(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const cfg = await getProtocolConfig();
      setConfig(cfg);
      if (cfg.enabled) {
        const data = await listProtocols();
        setProtocols(data.protocols || []);
      }
    } catch (err) {
      toast.error('Could not load protocols');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  // Entitlement gate — the clinic hasn't been switched on by a super admin.
  if (!config?.enabled) {
    return (
      <Box sx={{ maxWidth: 640, mx: 'auto', mt: 6, textAlign: 'center' }}>
        <Box sx={{ width: 72, height: 72, borderRadius: 3, mx: 'auto', mb: 2, display: 'flex', alignItems: 'center',
          justifyContent: 'center', background: 'linear-gradient(135deg,#0d9488,#14b8a6)', color: '#fff' }}>
          <LockIcon fontSize="large" />
        </Box>
        <Typography variant="h5" fontWeight={700}>Protocol Development</Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          This feature isn't enabled for your clinic yet. Ask your administrator to request access —
          a ScribeLive super admin switches it on per clinic.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* A row on a phone could not work: neither the heading nor the button can
          shrink, so their combined minimum width pushed the whole page 40px past the
          viewport — the button, the status chips and the right edge of every card were
          cut off. It stacks below `sm` instead. */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h4" fontWeight={600}>Protocol Development</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            Author clinical protocols with AI — capture a working session, or co-author from a brief.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          New protocol
        </Button>
      </Stack>

      {protocols.length === 0 ? (
        <Card variant="outlined" sx={{ p: 5, textAlign: 'center', borderStyle: 'dashed' }}>
          <ChatIcon sx={{ fontSize: 44, color: 'primary.main', mb: 1 }} />
          <Typography variant="h6" fontWeight={700}>No protocols yet</Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Create your first protocol and build it section by section with the AI.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>New protocol</Button>
        </Card>
      ) : (
        <Grid container spacing={2.5}>
          {protocols.map((p) => {
            const v = p.head_version || {};
            return (
              <Grid item xs={12} sm={6} md={4} key={p.id}>
                <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardActionArea onClick={() => navigate(`/protocols/${p.id}`)} sx={{ flexGrow: 1 }}>
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <ProtocolIcon color="primary" />
                        <Stack direction="row" spacing={0.5}>
                          <Chip size="small" label={v.version || 'v1'} variant="outlined" />
                          <Chip size="small" label={(v.state || 'draft').replace('_', ' ')}
                            color={STATE_COLOR[v.state] || 'default'} />
                        </Stack>
                      </Stack>
                      <Typography variant="h6" fontWeight={700} noWrap>{p.name}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{
                        mt: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: 40,
                      }}>
                        {v.summary || p.description || 'Draft in progress…'}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                  {isClinicAdmin && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, py: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Tooltip title="Delete protocol">
                        <IconButton size="small" onClick={() => setConfirmDelete(p)}
                          sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <CreateDialog open={dialogOpen} onClose={() => setDialogOpen(false)}
        onCreated={(p) => { setDialogOpen(false); navigate(`/protocols/${p.id}`); }} />

      <Dialog open={Boolean(confirmDelete)} onClose={deleting ? undefined : () => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={700}>Delete protocol?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This permanently deletes <strong>{confirmDelete?.name}</strong> and all of its versions,
            sections, questions and history. This can't be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDelete(null)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
